import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";

const BLANK = {
  title: "", slug: "", content: "", excerpt: "", cover_image_url: "",
  category: "General", meta_description: "", meta_keywords: "", og_image_url: "",
  publish_at: "", published: false,
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => setPosts((await api.get("/blog?published_only=false")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/blog/${editing.id}`, editing);
      else await api.post("/blog", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/blog/${id}`); load();
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Blog</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">SEO content</h1>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} className="btn-primary inline-flex items-center gap-2" data-testid="new-post-btn">
          <Plus size={16} /> New post
        </button>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-zinc-50 text-zinc-500 text-xs">
            <tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100" data-testid={`post-row-${p.id}`}>
                <td className="px-4 py-3 font-semibold">{p.title}</td>
                <td className="px-4 py-3">{p.category}</td>
                <td className="px-4 py-3">{p.published ? <span className="text-green-600">Published</span> : <span className="text-zinc-400">Draft</span>}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing({ ...p })} className="p-1.5 hover:bg-zinc-100 rounded"><Pencil size={14} /></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 sm:p-6" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg sm:text-xl">{editing.id ? "Edit post" : "New post"}</div>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input className="input" placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="post-title" />
              <input className="input" placeholder="Slug (auto if empty)" value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="post-slug" />
              <div>
                <label className="text-xs font-semibold text-zinc-600 block mb-1">Cover image</label>
                <ImageUpload value={editing.cover_image_url || ""} onChange={(url) => setEditing({ ...editing, cover_image_url: url })} testid="post-cover-image" />
              </div>
              <input className="input" placeholder="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
              <textarea className="input" rows={2} placeholder="Excerpt" value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
              <textarea className="input" rows={10} placeholder="Content (Markdown or plain text)" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid="post-content" />
              <input className="input" placeholder="Meta description (SEO)" value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} data-testid="post-meta-desc" />
              <input className="input" placeholder="Meta keywords (SEO)" value={editing.meta_keywords || ""} onChange={(e) => setEditing({ ...editing, meta_keywords: e.target.value })} />
              <input className="input" placeholder="OG image URL (share card)" value={editing.og_image_url || ""} onChange={(e) => setEditing({ ...editing, og_image_url: e.target.value })} data-testid="post-og-image" />
              <div>
                <label className="text-xs font-semibold text-zinc-600 block mb-1">Publish at (optional — schedules the post)</label>
                <input className="input" type="datetime-local" value={editing.publish_at?.slice(0, 16) || ""}
                       onChange={(e) => setEditing({ ...editing, publish_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                       data-testid="post-publish-at" />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="save-post-btn">Save</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.input { width:100%; border:1px solid #e4e4e7; border-radius:0.5rem; padding:0.5rem 0.75rem; }`}</style>
    </div>
  );
}
